import { Spinner } from '@1trading/react';
import { useEffect, useRef } from 'react';

const PRELOAD_ROOT_MARGIN = '200px';

interface InfiniteScrollSentinelProps {
  onLoadMore: () => void;
  hasMore: boolean;
  isLoading: boolean;
}

/**
 * IntersectionObserver-driven "load more" trigger for cursor/LEK-paginated
 * lists (replaces the legacy v-infinite-scroll). Render it directly below the
 * table; it fires onLoadMore when scrolled near, preloading 200px early.
 */
export const InfiniteScrollSentinel = ({ onLoadMore, hasMore, isLoading }: InfiniteScrollSentinelProps) => {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = sentinelRef.current;
    if (!element || !hasMore || isLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) onLoadMore();
      },
      { rootMargin: PRELOAD_ROOT_MARGIN },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [hasMore, isLoading, onLoadMore]);

  return (
    <div className="flex items-center justify-center py-4" ref={sentinelRef}>
      {isLoading ? <Spinner className="size-5" /> : null}
      {!hasMore && !isLoading ? <span className="text-xs text-muted-foreground">End of results</span> : null}
    </div>
  );
};
InfiniteScrollSentinel.displayName = 'InfiniteScrollSentinel';
